/**
 * Employee-side SUI / SDI / local income tax presets — 2026 approximations.
 *
 * Values are **decimal fractions** (0.011 = 1.1%) matching the backend
 * `sui_rate` / `sdi_rate` / `local_tax_rate` schema.
 *
 * These are best-effort SUGGESTIONS for the most common cases and should
 * always be verified with a CPA. Only states where the employee bears a
 * meaningful portion (or where a headline local tax exists) are listed —
 * everywhere else the preset is all zeros and no hint is shown.
 *
 * Sources: state DOL/DOT bulletins, IRS Pub 15 state supplement, city
 * revenue portals. Local rates default to the largest city's resident rate.
 */
export const STATE_TAX_PRESETS = {
  AK: { sui_rate: 0.005,  sdi_rate: 0,       local_tax_rate: 0,       note: "Alaska SUI 0.50% (employee portion)" },
  CA: { sui_rate: 0,      sdi_rate: 0.011,   local_tax_rate: 0,       note: "California SDI 1.10%" },
  HI: { sui_rate: 0,      sdi_rate: 0.005,   local_tax_rate: 0,       note: "Hawaii TDI 0.50% (max, employee portion)" },
  NJ: { sui_rate: 0.00425, sdi_rate: 0.0009, local_tax_rate: 0,       note: "NJ SUI 0.425% + SDI 0.09%" },
  NY: { sui_rate: 0,      sdi_rate: 0.005,   local_tax_rate: 0.03876, note: "NY SDI 0.50% + NYC resident local 3.876% (set 0 if outside NYC)" },
  PA: { sui_rate: 0.0007, sdi_rate: 0,       local_tax_rate: 0.0375,  note: "PA SUI 0.07% + Philadelphia resident 3.75% (adjust for your city)" },
  RI: { sui_rate: 0,      sdi_rate: 0.013,   local_tax_rate: 0,       note: "Rhode Island TDI 1.30%" },
  OH: { sui_rate: 0,      sdi_rate: 0,       local_tax_rate: 0.025,   note: "OH local income tax ~2.5% (Columbus/Cleveland typical — adjust)" },
  MD: { sui_rate: 0,      sdi_rate: 0,       local_tax_rate: 0.032,   note: "Maryland county tax ~3.20% (varies by county)" },
  IN: { sui_rate: 0,      sdi_rate: 0,       local_tax_rate: 0.015,   note: "Indiana county tax ~1.50% (varies by county)" },
  KY: { sui_rate: 0,      sdi_rate: 0,       local_tax_rate: 0.02,    note: "KY local occupational tax ~2.0% (Louisville/Lexington)" },
  MI: { sui_rate: 0,      sdi_rate: 0,       local_tax_rate: 0.024,   note: "MI city income tax ~2.40% (Detroit resident)" },
  MO: { sui_rate: 0,      sdi_rate: 0,       local_tax_rate: 0.01,    note: "MO earnings tax 1.0% (Kansas City / St. Louis)" },
};

/** Returns the preset object for a state, or null if none defined. */
export function getStatePreset(state) {
  if (!state) return null;
  return STATE_TAX_PRESETS[state.toUpperCase()] || null;
}

/** Fields the presets cover — safe to iterate in the form. */
export const PRESET_KEYS = ["sui_rate", "sdi_rate", "local_tax_rate"];
